import { HttpErrorResponse } from '@angular/common/http';

/**
 * Extracts a user-facing message from an HTTP or API error, checking a raw string body,
 * then `errorMessage`, then `message`, then the first entry of `errors` (or PascalCase `Errors`), before returning the fallback.
 *
 * @param error The HTTP error response or unknown catch value to inspect.
 * @param fallbackMessage Message returned when the error carries no usable message.
 * @returns The best available human-readable error message.
 */
export function extractHttpErrorMessage(error: unknown, fallbackMessage: string): string {
  const apiError: unknown = unwrapErrorBody(error);

  if (typeof apiError === 'string' && apiError.trim()) {
    return apiError;
  }

  if (apiError && typeof apiError === 'object') {
    const record: { errorMessage?: unknown; message?: unknown; errors?: unknown; Errors?: unknown } = apiError as {
      errorMessage?: unknown;
      message?: unknown;
      errors?: unknown;
      Errors?: unknown;
    };

    if (typeof record.errorMessage === 'string' && record.errorMessage.trim()) {
      return record.errorMessage;
    }

    if (typeof record.message === 'string' && record.message.trim()) {
      return record.message;
    }

    const errorList: unknown = record.errors ?? record.Errors;
    const firstError: unknown = Array.isArray(errorList) ? errorList[0] : undefined;
    if (typeof firstError === 'string' && firstError.trim()) {
      return firstError;
    }
  }

  return fallbackMessage;
}

/**
 * Pulls the API body off an {@link HttpErrorResponse} or a duck-typed `{ error }` object.
 *
 * @param error Caught error value.
 * @returns The nested `error` payload when present, otherwise the original value.
 */
function unwrapErrorBody(error: unknown): unknown {
  if (error instanceof HttpErrorResponse) {
    return error.error;
  }

  if (error && typeof error === 'object' && 'error' in error) {
    return (error as { error: unknown }).error;
  }

  return error;
}
